import { TFunction, useTranslation } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { Anchor } from "@impero-com/ui";

import type {
    TestingActivityCurrentStep,
    TestingActivityKind,
} from "api/generated/model";

import {
    FirstReviewerSubmissionOption,
    SecondReviewerSubmissionOption,
} from "./schema";

export const TESTING_ACTIVITY_DESCRIPTION_ANCHOR =
    "control-testing-collect-description";
export const TESTING_ACTIVITY_RESPONSIBLE_ANCHOR =
    "control-testing-collect-responsible";
export const TESTING_ACTIVITY_REVIEWER_ANCHOR =
    "control-testing-collect-reviewer";
export const TESTING_ACTIVITY_SECOND_REVIEWER_ANCHOR =
    "control-testing-collect-second-reviewer";

// The order MATTERS
export const TESTING_ACTIVITY_ANCHORS = [
    TESTING_ACTIVITY_DESCRIPTION_ANCHOR,
    TESTING_ACTIVITY_RESPONSIBLE_ANCHOR,
    TESTING_ACTIVITY_REVIEWER_ANCHOR,
    TESTING_ACTIVITY_SECOND_REVIEWER_ANCHOR,
] as const;

export type ControlTestingCollectAnchor =
    (typeof TESTING_ACTIVITY_ANCHORS)[number];

export function useControlTestingCollectAnchorLabels(
    currentStep: TestingActivityCurrentStep
): Record<ControlTestingCollectAnchor, string> {
    const { t } = useTranslation();
    const getResponsibleLabel = () => {
        switch (currentStep) {
            case "responsible":
                return t("ControlTestingActivityView_PerformResponsible_Title");
            case "firstReviewer":
            case "secondReviewer":
            case "completed":
                return t("ControlTestingActivityView_Responsible_Title");
            default:
                assertNever(currentStep);
        }
    };
    return {
        [TESTING_ACTIVITY_RESPONSIBLE_ANCHOR]: getResponsibleLabel(),
        [TESTING_ACTIVITY_DESCRIPTION_ANCHOR]: t(
            "ControlTestingActivityView_Description_Title"
        ),
        [TESTING_ACTIVITY_REVIEWER_ANCHOR]: t(
            "ControlTestingActivityView_FirstReviewer_Title"
        ),
        [TESTING_ACTIVITY_SECOND_REVIEWER_ANCHOR]: t(
            "ControlTestingActivityView_SecondReviewer_Title"
        ),
    };
}

export function useControlTestingCollectAnchors(
    activityKind: TestingActivityKind,
    currentStep: TestingActivityCurrentStep
): Anchor[] {
    const getAnchors = () => {
        switch (activityKind.kind) {
            case "ResponsibleOnly":
                return TESTING_ACTIVITY_ANCHORS.filter(
                    (anchor) =>
                        anchor !== TESTING_ACTIVITY_REVIEWER_ANCHOR &&
                        anchor !== TESTING_ACTIVITY_SECOND_REVIEWER_ANCHOR
                );
            case "WithFirstReviewer":
                return TESTING_ACTIVITY_ANCHORS.filter(
                    (anchor) =>
                        anchor !== TESTING_ACTIVITY_SECOND_REVIEWER_ANCHOR
                );
            case "WithSecondReviewer":
                return TESTING_ACTIVITY_ANCHORS;
            default:
                assertNever(activityKind);
        }
    };
    const anchorLabels = useControlTestingCollectAnchorLabels(currentStep);

    return getAnchors().map((anchor) => ({
        elementId: anchor,
        label: anchorLabels[anchor],
    }));
}

export interface FirstReviewerOptions {
    value: FirstReviewerSubmissionOption;
    label: string;
}
export function getFirstReviewerSubmissionOptions(
    activityKind: TestingActivityKind,
    t: TFunction
): FirstReviewerOptions[] {
    const getAcceptLabel = () => {
        switch (activityKind.kind) {
            case "WithSecondReviewer":
                return t(
                    "ControlTestingActivityView_FirstReviewerSendToSecond"
                );
            case "ResponsibleOnly":
            case "WithFirstReviewer":
                return t("ControlTestingActivityView_FirstReviewerAccept");
            default:
                assertNever(activityKind);
        }
    };

    return [
        {
            value: "firstReviewerAccept",
            label: getAcceptLabel(),
        },
        {
            value: "firstReviewerReject",
            label: t("ControlTestingActivityView_FirstReviewerReject"),
        },
    ];
}

export interface SecondReviewerOptions {
    value: SecondReviewerSubmissionOption;
    label: string;
}
export function getSecondReviewerSubmissionOptions(
    t: TFunction
): SecondReviewerOptions[] {
    return [
        {
            value: "secondReviewerAccept",
            label: t("ControlTestingActivityView_SecondReviewerAccept"),
        },
        {
            value: "secondReviewerReject",
            label: t("ControlTestingActivityView_SecondReviewerReject"),
        },
        {
            value: "secondReviewerRejectToResponsible",
            label: t(
                "ControlTestingActivityView_SecondReviewerRejectToResponsible"
            ),
        },
    ];
}
